import Map, { NavigationControl } from "react-map-gl/mapbox";
import { MapVehicle } from "./mapVehicle/MapVehicle";
import { useEffect, useRef } from "react";
import Text from "../text/Text";
import BusIcon from "./img/bus.svg";
import CloseIcon from "../../image/xButton.svg";
import Badge from "../badge/Badge";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import { formatETATime } from "../../config/utilities/fDate";
import BlueDot from "./img/blueDot.svg";
import GreenDot from "./img/greenDot.svg";
import YellowDot from "./img/yellowDot.svg";
import ClockIcon from "./img/clock.svg";
import LocationIcon from "./img/location.svg";
import SpeedIcon from "./img/speed.svg";
import CarIcon from "./img/car.svg";
import BrandIcon from "./img/brand.svg";
import ColorIcon from "./img/color.svg";
import CapacityIcon from "./img/capacity.svg";
import CalendarIcon from "../datePicker/img/calendar.svg";
import { SecondaryButton } from "../buttonComponent/ButtonComponent";
import Dropdown from "../dropdown/Dropdown";
import LoadingSpinner from "../loading/LoadingSpinner";

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const LiveMap = ({
  width = "100%",
  height = "75vh",
  liveData = [],
  selectedVehicle,
  setSelectedVehicle,
  vehicleOptions = [],
  loading,
}) => {
  const mapRef = useRef(null);

  function getStatusStyle(status) {
    switch (status) {
      case "ongoing":
        return { background: "#EFF8FF", color: "#175CD3", icon: BlueDot };
      case "completed":
        return { background: "#ECFDF3", color: "#027A48", icon: GreenDot };
      case "at-final-stop":
        return { background: "#FFFAEB", color: "#B54708", icon: YellowDot };
      default:
        return { background: "#F2F4F7", color: "#667085" };
    }
  }

  useEffect(() => {
    if (!selectedVehicle || !mapRef.current) return;

    const selected = liveData.find(
      (item) => item.documentId === selectedVehicle
    );

    if (!selected) return;

    const coordinates = selected?.location?.coordinates;
    if (!Array.isArray(coordinates)) return;

    mapRef.current.flyTo({
      center: coordinates,
      zoom: 15,
      speed: 1.2,
      curve: 1.4,
      essential: true,
    });
  }, [selectedVehicle, liveData]);

  const handleSelect = (vehicle) => {
    setSelectedVehicle(vehicle.documentId);
  };

  const handleReset = () => setSelectedVehicle("");

  const vehicle = liveData.find((item) => item.documentId === selectedVehicle);


  const statusStyle = getStatusStyle(vehicle?.status);

  if (loading) {
    return (
      <div className="live-map-loading">
        <LoadingSpinner
          className="loading-spinner"
          size="large"
          color="#ffb502"
        />
      </div>
    );
  }

  return (
    <div
      style={{
        width: width,
        height: height,
        display: "flex",
        position: "relative",
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        initialViewState={{
          longitude: 101.69589,
          latitude: 3.14977,
          zoom: 10,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/asiamobiliti/ckb1yq6080opd1inb3lkdmbii"
      >
        <NavigationControl position="bottom-right" />
        {liveData.map((item) => (
          <MapVehicle
            key={item.documentId}
            data={item}
            onSelect={handleSelect}
          />
        ))}
      </Map>
      <div className="live-map-filter">
        <div className="live-map-dropdown">
          <Dropdown
            options={vehicleOptions}
            name="vehicle"
            placeholder="Select a vehicle..."
            onChangeFunction={(e) => setSelectedVehicle(e.target.value)}
            value={selectedVehicle}
            width="25rem"
            input={{}}
          />
        </div>
        <div className="live-map-clear-button">
          <SecondaryButton
            label="Clear"
            width="auto"
            onClickFunction={handleReset}
            disable={!selectedVehicle}
          />
        </div>
      </div>
      <div className={`live-map-panel ${selectedVehicle ? "open" : ""}`}>
        <div className="live-map-panel-header">
          <div className="live-map-panel-header-left">
            <img loading="lazy" src={BusIcon} alt="busIcon" />
            <Text
              text={
                vehicle?.vehicleDetails?.busNumber
                  ? `${vehicle?.vehicleDetails?.vehicleNumber} (${vehicle?.vehicleDetails?.busNumber})`
                  : vehicle?.vehicleDetails?.vehicleNumber
              }
              size="1rem"
              weight="600"
              color="#365A6D"
            />
            {vehicle?.status && (
              <Badge
                icon={statusStyle.icon}
                width="fit-content"
                text={formatCapitalize(vehicle?.status)}
                background={statusStyle.background}
                color={statusStyle.color}
              />
            )}
          </div>
          <div
            className="live-map-header-right"
            onClick={() => setSelectedVehicle("")}
          >
            <div className="close-button">
              <img src={CloseIcon} alt="close-modal" />
            </div>
          </div>
        </div>
        <div className="live-map-panel-body">
          <div className="panel-section panel-last-updated-section">
            <div className="panel-container">
              <img src={ClockIcon} alt="clock-icon" />
              <div>
                {vehicle?.updatedAt
                  ? `Last updated on ${new Date(vehicle.updatedAt)
                      .toLocaleString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                        timeZone: "Asia/Kuala_Lumpur",
                      })
                      .replace(",", "")}`
                  : "No update timestamp"}
              </div>
            </div>
          </div>
          <div className="panel-divider" />
          {vehicle?.tripName && (
            <>
              <div className="panel-section">
                <div className="panel-container">
                  <img src={CalendarIcon} alt="calendar-icon" />
                  <div>{vehicle.tripName}</div>
                </div>
              </div>
              <div className="panel-divider panel-divider-before-stops" />
            </>
          )}
          {(vehicle?.previousStop || vehicle?.nextStop || vehicle?.finalStop) && (
            <>
              <div className="panel-section panel-stops-section">
                <div className="panel-container stops-container">
                  <img src={LocationIcon} alt="location-icon" />
                  <div className="stops-timeline">
                    {vehicle?.previousStop && (
                      <div className="stop-item">
                        <div className="stop-label">Previous</div>
                        <div className="stop-name">{vehicle.previousStop.name}</div>
                      </div>
                    )}
                    {vehicle?.previousStop && (vehicle?.nextStop || vehicle?.finalStop) && (
                      <div className="stop-arrow">→</div>
                    )}
                    {vehicle?.nextStop && (
                      <div className="stop-item">
                        <div className="stop-label">Next</div>
                        <div className="stop-name">{vehicle.nextStop.name}</div>
                        {vehicle.nextStop.eta && (
                          <div className="stop-eta">
                            ETA: {formatETATime(vehicle.nextStop.eta).formattedTime}
                          </div>
                        )}
                      </div>
                    )}
                    {vehicle?.nextStop && vehicle?.finalStop && (
                      <div className="stop-arrow">→</div>
                    )}
                    {vehicle?.finalStop && (
                      <div className="stop-item">
                        <div className="stop-label">Final</div>
                        <div className="stop-name">{vehicle.finalStop.name}</div>
                        {vehicle.finalStop.eta && (
                          <div className="stop-eta">
                            ETA: {formatETATime(vehicle.finalStop.eta).formattedTime}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="panel-divider panel-divider-after-stops" />
            </>
          )}
          <div className="panel-section">
            <div className="panel-container location-info">
              <img src={LocationIcon} alt="location-icon" />
              <div>
                <div className="panel-address-text">{vehicle?.address}</div>
                <div className="panel-coordinates-text">
                  {vehicle?.location?.coordinates?.join(", ") ||
                    "No coordinates available"}
                </div>
              </div>
            </div>
            <div className="panel-container panel-speed-container">
              <img src={SpeedIcon} alt="speed-icon" />
              <div>{vehicle?.speed} km/h</div>
            </div>
          </div>
          <div className="panel-divider" />
          <div className="panel-section">
            <div className="panel-container-section">
              <div className="panel-container panel-model-container">
                <div className="panel-container-info">
                  <img
                    src={CarIcon}
                    alt="car-icon"
                    className="panel-info-icon"
                  />
                  <div className="panel-info-text">
                    <div className="panel-container-info-title">Model</div>
                    <div className="panel-container-info-data">
                      {vehicle?.vehicleDetails?.model}
                    </div>
                  </div>
                </div>
              </div>
              <div className="panel-container panel-brand-container">
                <div className="panel-container-info">
                  <img
                    src={BrandIcon}
                    alt="brand-icon"
                    className="panel-info-icon"
                  />
                  <div className="panel-info-text">
                    <div className="panel-container-info-title">Brand</div>
                    <div className="panel-container-info-data">
                      {vehicle?.vehicleDetails?.brand}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="panel-container-section">
              <div className="panel-container panel-color-container">
                <div className="panel-container-info">
                  <img
                    src={ColorIcon}
                    alt="color-icon"
                    className="panel-info-icon"
                  />
                  <div className="panel-info-text">
                    <div className="panel-container-info-title">Color</div>
                    <div className="panel-container-info-data">
                      {vehicle?.vehicleDetails?.color}
                    </div>
                  </div>
                </div>
              </div>
              <div className="panel-container panel-capacity-container">
                <div className="panel-container-info">
                  <img
                    src={CapacityIcon}
                    alt="capacity-icon"
                    className="panel-info-icon"
                  />
                  <div className="panel-info-text">
                    <div className="panel-container-info-title">Capacity</div>
                    <div className="panel-container-info-data">
                      {`${vehicle?.occupancy?.currentOccupancy}/${vehicle?.occupancy?.maxCapacity}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMap;
